import React, { useState, useEffect } from "react";
import { Save, User, Mail, Shield, Activity, Calendar } from "lucide-react";
import FormHeader from "../Common/FormHeader"; 

const FormUser = ({ onBack, onSave, initialData = null }) => {
  // Definisi State Awal untuk User Management
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "User",
    status: "Active",
    joinedDate: "",
    bio: ""
  });

  // Sinkronisasi data saat Edit Mode sesuai pola workshop
  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        name: initialData.name || "",
        email: initialData.email || "",
        role: initialData.role || "User",
        status: initialData.status || "Active",
        joinedDate: initialData.joinedDate || "",
        bio: initialData.bio || ""
      });
    } else {
      setFormData({
        name: "", email: "", role: "User",
        status: "Active", joinedDate: "", bio: ""
      });
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="p-6 space-y-6 bg-white dark:bg-slate-900 rounded-[2rem] shadow-sm border border-stone-100 dark:border-white/5 animate-in fade-in duration-500">
      
      {/* HEADER DENGAN TOMBOL BACK */}
      <FormHeader 
        title={initialData ? "Edit User" : "Create New User"}
        subtitle="Manage user access levels and profile information"
        onBack={onBack} 
      />

      <form onSubmit={handleSubmit} className="space-y-8 max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          <div className="space-y-6">
            {/* Full Name */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Full Name</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input 
                  type="text" required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})} 
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                  placeholder="e.g. Andri Sebastian"
                />
              </div>
            </div>

            {/* Email Address */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input 
                  type="email" required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                  placeholder="andri@example.com"
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              {/* Role Selection */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Role</label>
                <div className="relative">
                  <Shield className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                  <select 
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white appearance-none"
                  >
                    <option value="Admin">Admin</option>
                    <option value="Speaker">Speaker</option>
                    <option value="User">User</option>
                  </select>
                </div>
              </div>

              {/* Status Selection */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Status</label>
                <div className="relative">
                  <Activity className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                  <select 
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white appearance-none"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Suspended">Suspended</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Joined Date */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Joined Date</label>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input 
                  type="date"
                  value={formData.joinedDate}
                  onChange={(e) => setFormData({...formData, joinedDate: e.target.value})}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* User Bio / Notes */}
          <div className="md:col-span-2 flex flex-col gap-1.5">
            <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">User Biography / Internal Notes</label>
            <textarea 
              rows="4"
              value={formData.bio}
              onChange={(e) => setFormData({...formData, bio: e.target.value})}
              className="w-full px-4 py-4 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none resize-none leading-relaxed focus:border-blue-500 text-slate-800 dark:text-white"
              placeholder="Enter brief description or staff notes about this user..."
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-6 border-t border-stone-50 dark:border-white/5 flex justify-end gap-3">
          <button 
            type="button" 
            onClick={onBack}
            className="px-8 py-3 rounded-xl text-sm font-semibold text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-white/5 transition-all"
          >
            Discard
          </button>
          <button 
            type="submit"
            className="px-10 py-3 bg-[#0F172A] dark:bg-blue-600 text-white rounded-xl text-sm font-semibold shadow-lg hover:bg-slate-800 dark:hover:bg-blue-700 transition-all flex items-center gap-2"
          >
            <Save size={18} />
            {initialData ? "Update User" : "Save User"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormUser;